import { Component, computed, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ExtendedFileMetadata } from '@lib/models/extended-file-metadata';
import {
  DerStateTransitionTrigger,
  EtatQpi,
  EtatRecueil,
  FileMetadata,
  GedCategory,
  GedDocumentType,
  QpiStateTransitionTrigger,
  RecueilStateTransitionTrigger,
  SignatureState,
} from '@lib/models/generated/graphql';
import { DocumentsService } from '@lib/services/documents.service';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { currentDatePivotYear, getRawFileId } from '@lib/utils/app-utils';
import { requiredSignatureDateErrors } from '@lib/utils/errors-messages';
import { minDate, minDateValidator } from '@lib/validators/min-date.validator';
import { getLastYearDate, minSignatureDateValidator } from '@lib/validators/min-signature-date.validator';

@Component({
  selector: 'app-replace-document-dialog',
  imports: [SHARED_IMPORTS],
  templateUrl: './replace-document-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './replace-document-dialog.component.scss',
})
export class ReplaceDocumentDialogComponent implements OnInit {
  public documentService = inject(DocumentsService);
  public snackbarService = inject(OuiSnackbarService);
  public investisseurService = inject(InvestisseurService);
  public dialogRef = inject(MatDialogRef<ReplaceDocumentDialogComponent>);
  private destroyRef = inject(DestroyRef);
  public data = inject<{
    previousDocument: ExtendedFileMetadata;
  }>(MAT_DIALOG_DATA);
  public signatureDateControl = new FormControl<string | null>(null, Validators.required);
  public saving = signal<boolean>(false);
  public acceptedFileType =
    '.pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';
  public fileControl = new FormControl<File[]>([]);
  public investisseurDocuments = signal<FileMetadata[]>([]);
  public derSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(doc => doc.typeDocumentCode === GedDocumentType.Der && doc.etatSignature === SignatureState.Pending)
  );
  public recueilSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.RecInfo && doc.etatSignature === SignatureState.Pending
    )
  );
  public qpiSignatureInProgress = computed<boolean>(() =>
    this.investisseurDocuments().some(
      doc => doc.typeDocumentCode === GedDocumentType.ProfInvest && doc.etatSignature === SignatureState.Pending
    )
  );
  public recueilImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.recueilEtat === EtatRecueil.ACompleter
  );
  public qpiImcomplete = computed<boolean>(
    () => this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.qpiEtat === EtatQpi.ACompleter
  );

  public maxSignatureDate = signal<Date>(new Date());
  public minSignatureDate = computed<Date>(() => {
    let date;
    if (this.lastYearSignatureDate()) {
      date = getLastYearDate();
      date.setDate(date.getDate() + 1);
    } else {
      date = minDate;
    }
    return date;
  });
  public lastYearSignatureDate = signal<boolean>(false);
  readonly dateErrors = requiredSignatureDateErrors;
  readonly currentDatePivotYear = currentDatePivotYear();

  ngOnInit(): void {
    this.investisseurService.investisseurDocuments$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(docs => {
      this.investisseurDocuments.set(docs ?? []);
    });
    if (this.data.previousDocument.typeDocumentCode) {
      this.lastYearSignatureDate.set(
        [GedDocumentType.RecInfo, GedDocumentType.ProfInvest].includes(this.data.previousDocument.typeDocumentCode as GedDocumentType)
      );
      this.signatureDateControl.setValidators([
        Validators.required,
        this.lastYearSignatureDate() ? minSignatureDateValidator : minDateValidator,
      ]);
    }
  }

  public deleteFile(): void {
    this.fileControl.patchValue([]);
  }

  public async replaceFile(): Promise<void> {
    this.saving.set(true);
    const previousDocument = this.data.previousDocument;
    const result = await this.documentService.deleteDocument(previousDocument.baseFileId);
    if (result) {
      const file = this.fileControl.value?.[0] as File;
      const signatureDate = this.signatureDateControl.value ? new Date(this.signatureDateControl.value as string) : undefined;
      const fileId = await this.documentService.createFileAndUpload(
        file,
        file.name as string,
        previousDocument.investisseurIds as number[],
        previousDocument.denomination as string,
        previousDocument.typeDocumentCode as GedDocumentType,
        this.getDateFinValidite(this.signatureDateControl),
        signatureDate,
        true,
        previousDocument.dateDeReference ?? undefined,
        previousDocument.tags ?? undefined,
        (previousDocument.categoryCode as GedCategory) ?? undefined
      );
      if (fileId) {
        const rawFileId = getRawFileId(fileId);
        if (previousDocument.typeDocumentCode === GedDocumentType.Der) {
          previousDocument.investisseurIds?.map(id =>
            this.investisseurService.updateDERState(
              DerStateTransitionTrigger.ConsultantAddsOrReplaceBySignedDer,
              false,
              rawFileId,
              id,
              signatureDate
            )
          );
        }
        if (previousDocument.typeDocumentCode === GedDocumentType.RecInfo) {
          previousDocument.investisseurIds?.map(id =>
            this.investisseurService.updateRecueilState(
              RecueilStateTransitionTrigger.ConsultantHasUploadedSignedRecueil,
              rawFileId,
              id,
              undefined,
              signatureDate
            )
          );
        }
        if (previousDocument.typeDocumentCode === GedDocumentType.ProfInvest) {
          previousDocument.investisseurIds?.map(id =>
            this.investisseurService.updateQPIState(QpiStateTransitionTrigger.ConsultantHasUploadedSignedQpi, rawFileId, id, signatureDate)
          );
        }
        this.snackbarService.open('Votre document a été remplacé', 'success');
        this.dialogRef.close(true);
        this.saving.set(false);
      }
    }
  }

  private getDateFinValidite(dateFormControl: FormControl<string | null>): Date | undefined {
    const expirationDate = new Date(dateFormControl.value as string);
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    return new Date(expirationDate);
  }
}
